import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './EditNotesModal.css';

const EditNotesModal = ({ isOpen, onClose, notes = '', onSave, title = 'Edit Notes' }) => {
  const [editedNotes, setEditedNotes] = useState(notes);
  const [wordCount, setWordCount] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);
  const textareaRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [lastTypedAt, setLastTypedAt] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const autoSaveTimeoutRef = useRef(null);

  // Update initial notes content when prop changes
  useEffect(() => {
    if (isOpen) {
      setEditedNotes(notes);
      updateCounts(notes);
      document.body.classList.add('modal-open');
      
      // Add a small timeout to prevent immediate backdrop clicks
      const timeoutId = setTimeout(() => {
        // This helps prevent accidental immediate clicks on the backdrop
      }, 10);
      
      return () => {
        clearTimeout(timeoutId);
        // Clear any auto-save timeout when closing
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
      };
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
      // Clear any auto-save timeout when unmounting
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [isOpen, notes]);
  
  // Handle auto-save functionality
  useEffect(() => {
    // Only proceed if the user has typed something
    if (lastTypedAt && editedNotes !== notes) {
      // Clear any existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      // Set a new timeout for auto-save after 5 seconds
      setAutoSaveStatus('Changes will be saved automatically...');
      autoSaveTimeoutRef.current = setTimeout(() => {
        onSave(editedNotes);
        setAutoSaveStatus('All changes saved');
        
        // Clear status message after 2 seconds
        setTimeout(() => {
          setAutoSaveStatus('');
        }, 2000);
      }, 5000);
    }
    
    // Clean up on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [lastTypedAt, editedNotes, notes, onSave]);

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  const handleSave = () => {
    onSave(editedNotes);
    onClose();
  };

  const updateCounts = (text) => {
    if (text) {
      setCharacterCount(text.length);
      setWordCount(text.trim() === '' ? 0 : text.trim().split(/\s+/).length);
    } else {
      setCharacterCount(0);
      setWordCount(0);
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setEditedNotes(newText);
    updateCounts(newText);
    // Set timestamp for auto-save feature
    setLastTypedAt(new Date());
  };


  const handleKeyDown = (e) => {
    // Close on Escape key (unless in fullscreen)
    if (e.key === 'Escape' && !isFullScreen) {
      onClose();
    }
    // Exit fullscreen on Escape when in fullscreen
    if (e.key === 'Escape' && isFullScreen) {
      setIsFullScreen(false);
      e.preventDefault();
    }
    // Save on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
    // Tab key handling for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      
      // Insert tab at the cursor position
      const newText = editedNotes.substring(0, start) + '    ' + editedNotes.substring(end);
      setEditedNotes(newText);
      updateCounts(newText);
      
      // Move cursor after the inserted tab
      setTimeout(() => {
        e.target.selectionStart = start + 4;
        e.target.selectionEnd = start + 4;
      }, 0);
    }
    
    // Bold with Ctrl+B
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      formatText('**', '**');
    }
    
    // Italic with Ctrl+I
    if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
      e.preventDefault();
      formatText('_', '_');
    }
    
    // Underline with Ctrl+U (using markdown-style formatting)
    if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
      e.preventDefault();
      formatText('<u>', '</u>');
    }
  };
  
  const formatText = (prefix, suffix) => {
    const textarea = textareaRef.current;
    if (!textarea) return; // Add null check
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Get selected text
    const selectedText = editedNotes.substring(start, end);
    
    // If text is selected, wrap it with formatting
    if (start !== end) {
      const newText = editedNotes.substring(0, start) + prefix + selectedText + suffix + editedNotes.substring(end);
      setEditedNotes(newText);
      updateCounts(newText);
      
      // Set cursor position after the formatted text
      setTimeout(() => {
        if (textarea) { // Check again in case it was unmounted
          textarea.selectionStart = end + prefix.length + suffix.length;
          textarea.selectionEnd = end + prefix.length + suffix.length;
        }
      }, 0);
    } else {
      // If no text is selected, insert formatting markers and place cursor between them
      const newText = editedNotes.substring(0, start) + prefix + suffix + editedNotes.substring(end);
      setEditedNotes(newText);
      updateCounts(newText);
      
      // Place cursor between the formatting markers
      setTimeout(() => {
        if (textarea) { // Check again in case it was unmounted
          textarea.selectionStart = start + prefix.length;
          textarea.selectionEnd = start + prefix.length;
        }
      }, 0);
    }
  };
  
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };
  
  const handleBulletList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return; // Add null check
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Get selected text
    const selectedText = editedNotes.substring(start, end);
    
    if (selectedText.includes('\n') || start === 0 || editedNotes[start-1] === '\n') {
      // Format multiple lines or at the beginning of a line
      const formattedText = selectedText
        .split('\n')
        .map(line => line.trim() ? `• ${line}` : line)
        .join('\n');
      
      const newText = editedNotes.substring(0, start) + formattedText + editedNotes.substring(end);
      setEditedNotes(newText);
      updateCounts(newText);
    } else {
      // We're in the middle of a line, insert a bullet at the beginning of the line
      let lineStart = start;
      while (lineStart > 0 && editedNotes[lineStart-1] !== '\n') {
        lineStart--;
      }
      
      const newText = editedNotes.substring(0, lineStart) + '• ' + editedNotes.substring(lineStart);
      setEditedNotes(newText);
      updateCounts(newText);
      
      // Set cursor position after the inserted bullet
      setTimeout(() => {
        if (textarea) { // Check again in case it was unmounted
          textarea.selectionStart = end + 2;
          textarea.selectionEnd = end + 2;
        }
      }, 0);
    }
  };
  
  const handleNumberedList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return; // Add null check
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Get selected text
    const selectedText = editedNotes.substring(start, end);
    
    if (selectedText.includes('\n') || start === 0 || editedNotes[start-1] === '\n') {
      // Format multiple lines or at the beginning of a line
      const lines = selectedText.split('\n');
      const formattedText = lines
        .map((line, i) => line.trim() ? `${i+1}. ${line}` : line)
        .join('\n');
      
      const newText = editedNotes.substring(0, start) + formattedText + editedNotes.substring(end);
      setEditedNotes(newText);
      updateCounts(newText);
    } else {
      // We're in the middle of a line, insert a number at the beginning of the line
      let lineStart = start;
      while (lineStart > 0 && editedNotes[lineStart-1] !== '\n') {
        lineStart--;
      }
      
      const newText = editedNotes.substring(0, lineStart) + '1. ' + editedNotes.substring(lineStart);
      setEditedNotes(newText);
      updateCounts(newText);
      
      // Set cursor position after the inserted number
      setTimeout(() => {
        if (textarea) { // Check again in case it was unmounted
          textarea.selectionStart = end + 3;
          textarea.selectionEnd = end + 3;
        }
      }, 0);
    }
  };

  // Create the modal content
  const modalContent = (
    <>
      {/* Backdrop - No longer closes on click */}
      <div className="notes-modal-backdrop" onClick={(e) => e.preventDefault()}></div>
      
      {/* Modal */}
      <div 
        className={`notes-modal ${isFullScreen ? 'fullscreen' : ''}`} 
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching backdrop
      >
        <div className="notes-modal-header">
          <h3>{title}</h3>
          <div className="notes-modal-controls">
            <button 
              className="notes-modal-control-btn" 
              onClick={toggleFullScreen} 
              title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              <i className={`fas fa-${isFullScreen ? 'compress' : 'expand'}`}></i>
            </button>
            <button className="notes-modal-close" onClick={onClose} title="Close">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <div className="notes-formatting-toolbar">
          <button 
            className="format-button" 
            onClick={() => formatText('**', '**')} 
            title="Bold (Ctrl+B)"
          >
            <i className="fas fa-bold"></i>
          </button>
          <button 
            className="format-button" 
            onClick={() => formatText('_', '_')} 
            title="Italic (Ctrl+I)"
          >
            <i className="fas fa-italic"></i>
          </button>
          <button 
            className="format-button" 
            onClick={() => formatText('<u>', '</u>')} 
            title="Underline (Ctrl+U)"
          >
            <i className="fas fa-underline"></i>
          </button>
          <div className="format-divider"></div>
          <button 
            className="format-button" 
            onClick={handleBulletList} 
            title="Bullet List"
          >
            <i className="fas fa-list-ul"></i>
          </button>
          <button 
            className="format-button" 
            onClick={handleNumberedList} 
            title="Numbered List"
          >
            <i className="fas fa-list-ol"></i>
          </button>
        </div>
        
        <div className="notes-modal-body" style={{ paddingBottom: '80px' }}>
          <textarea
            ref={textareaRef}
            className="notes-textarea"
            value={editedNotes}
            onChange={handleTextChange}
            placeholder="Enter notes here..."
            autoFocus
          />
        </div>
        
        <div className="notes-modal-footer">
          <div className="notes-metadata">
            <span>{characterCount} characters</span>
            <span>{wordCount} words</span>
            {autoSaveStatus && (
              <span className="auto-save-status">{autoSaveStatus}</span>
            )}
          </div>
          <div className="keyboard-shortcuts">
            <span><kbd>Esc</kbd> to {isFullScreen ? "exit fullscreen" : "cancel"}</span>
            <span><kbd>Ctrl+Enter</kbd> to save</span>
          </div>
          <div className="modal-buttons">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>Save Notes</button>
          </div>
        </div>
      </div>
    </>
  );

  // Try to find the modal-root element
  const modalRoot = document.getElementById('modal-root');
  
  // If modal-root doesn't exist, create it dynamically
  if (!modalRoot) {
    console.warn("modal-root element not found, creating it dynamically");
    const newModalRoot = document.createElement('div');
    newModalRoot.id = 'modal-root';
    document.body.appendChild(newModalRoot);
  }
  
  return ReactDOM.createPortal(
    modalContent,
    document.getElementById('modal-root') || document.body
  );
};

export default EditNotesModal;